import { DEFAULT_FREE_PARTY_MEMBER_CAP, normalizePartyPlan } from "./party-entitlements";
import { getPartyPassCheckoutCancelPath } from "./party-pass-celebration";

/**
 * Party Pass did not exist before this instant, so splits before it cannot
 * be an alternative to paying. Metrics and MCP listings start here.
 */
export const PARTY_SPLIT_METRICS_SINCE = "2026-08-31T00:00:00.000Z";

export const SPLIT_PROMPT_CHECKOUT_SOURCE = "split_prompt" as const;

export type SplitPromptCheckoutSource = typeof SPLIT_PROMPT_CHECKOUT_SOURCE;

export type SplitPromptOutcome = "dismissed" | "paid";

export interface SplitCandidateParty {
  id: string;
  ownerId?: string;
  awardShowId: string;
  plan?: string | null;
  memberCount: number;
  memberCap: number | null;
  createdAt: Date | string;
  paidAt?: Date | string | null;
  blockedJoinCount?: number;
  mergedIntoPartyId?: string | null;
}

export interface CappedFreeOwnedParty {
  id: string;
  name: string;
  awardShowId: string;
  memberCount: number;
  memberCap: number;
}

export interface SplitBackfillParty {
  id: string;
  ownerId: string;
  ownerEmail: string;
  awardShowId: string;
  createdAt: Date | string;
  splitFromPartyId?: string | null;
}

export interface OwnedPartyForSplitPrompt {
  id: string;
  name: string;
  awardShowId: string;
  plan?: string | null;
  memberCount: number;
  memberCap: number | null;
  createdAt: Date | string;
  paidAt?: Date | string | null;
  splitFromPartyId?: string | null;
  mergedIntoPartyId?: string | null;
  blockedJoinCount?: number;
}

export type SplitPromptContext = {
  viewing: "original" | "split";
  checkoutPartyId: string;
  original: {
    id: string;
    name: string;
    memberCount: number;
    memberCap: number;
    plan: string;
  };
  split: {
    id: string;
    name: string;
    memberCount: number;
  };
};

export type PartyCapDemand = {
  freeAtCap: boolean;
  freeOverCap: boolean;
  paidOverCap: boolean;
};

export type SplitMergeMovePlan = {
  participantIdsToMove: string[];
  voteIdsToMove: string[];
  invitationIdsToMove: string[];
  blockedJoinIdsToMove: string[];
  reactionIdsToMove: string[];
};

function toTime(value: Date | string): number {
  const date = value instanceof Date ? value : new Date(value);
  return date.getTime();
}

export function partySplitMetricsSinceDate(): Date {
  return new Date(PARTY_SPLIT_METRICS_SINCE);
}

export function resolveSplitMetricsSince(
  since?: Date | string | null,
): Date {
  const floor = partySplitMetricsSinceDate();
  if (since == null || since === "") {
    return floor;
  }
  const requested = since instanceof Date ? since : new Date(since);
  if (!Number.isFinite(requested.getTime())) {
    return floor;
  }
  return requested.getTime() > floor.getTime() ? requested : floor;
}

export function isPartyCreatedOnOrAfterSplitMetricsStart(
  createdAt: Date | string,
): boolean {
  const createdMs = toTime(createdAt);
  if (!Number.isFinite(createdMs)) {
    return false;
  }
  return createdMs >= toTime(PARTY_SPLIT_METRICS_SINCE);
}

export function isExcludedFromSplitMetrics(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return normalized.includes("+bottester");
}

export function isMergedSplitParty(party: {
  mergedIntoPartyId?: string | null;
}): boolean {
  return Boolean(party.mergedIntoPartyId);
}

export function wasUnpaidAt(
  party: { paidAt?: Date | string | null },
  at: Date | string,
): boolean {
  if (!party.paidAt) {
    return true;
  }
  const paidMs = toTime(party.paidAt);
  const atMs = toTime(at);
  if (!Number.isFinite(paidMs) || !Number.isFinite(atMs)) {
    return true;
  }
  return paidMs > atMs;
}

export function isPartyAtFreeCap(party: {
  plan?: string | null;
  memberCount: number;
  memberCap: number | null;
  blockedJoinCount?: number;
}): boolean {
  if (!Number.isFinite(party.memberCount) || party.memberCount < 0) {
    return false;
  }

  const cap = party.memberCap ?? DEFAULT_FREE_PARTY_MEMBER_CAP;
  return (
    party.memberCount >= cap || (party.blockedJoinCount ?? 0) > 0
  );
}

/**
 * A split source is an earlier same-show party that was unpaid and at the
 * free cap when the new party was created.
 */
export function findSplitSourceParty(
  existingOwned: readonly SplitCandidateParty[],
  input: {
    awardShowId: string;
    createdAt?: Date | string;
    excludePartyId?: string;
  },
): SplitCandidateParty | null {
  const createdAt = input.createdAt ?? new Date();
  const createdMs = toTime(createdAt);
  if (!Number.isFinite(createdMs)) {
    return null;
  }

  const matches = existingOwned.filter((candidate) => {
    if (candidate.id === input.excludePartyId) {
      return false;
    }
    if (isMergedSplitParty(candidate)) {
      return false;
    }
    if (candidate.awardShowId !== input.awardShowId) {
      return false;
    }
    const candidateCreatedMs = toTime(candidate.createdAt);
    if (!Number.isFinite(candidateCreatedMs) || candidateCreatedMs >= createdMs) {
      return false;
    }
    if (!wasUnpaidAt(candidate, createdAt)) {
      return false;
    }
    return isPartyAtFreeCap(candidate);
  });

  if (matches.length === 0) {
    return null;
  }

  return matches.reduce((earliest, candidate) =>
    toTime(candidate.createdAt) < toTime(earliest.createdAt)
      ? candidate
      : earliest,
  );
}

export function planPartySplitBackfill(
  parties: readonly SplitBackfillParty[],
  candidates: readonly SplitCandidateParty[],
): Array<{ partyId: string; splitFromPartyId: string }> {
  const candidatesByOwner = new Map<string, SplitCandidateParty[]>();
  for (const candidate of candidates) {
    const ownerId = candidate.ownerId;
    if (!ownerId) {
      continue;
    }
    const list = candidatesByOwner.get(ownerId) ?? [];
    list.push(candidate);
    candidatesByOwner.set(ownerId, list);
  }

  const assignments: Array<{ partyId: string; splitFromPartyId: string }> = [];

  for (const party of parties) {
    if (party.splitFromPartyId) {
      continue;
    }
    if (!isPartyCreatedOnOrAfterSplitMetricsStart(party.createdAt)) {
      continue;
    }
    if (isExcludedFromSplitMetrics(party.ownerEmail)) {
      continue;
    }
    const source = findSplitSourceParty(candidatesByOwner.get(party.ownerId) ?? [], {
      awardShowId: party.awardShowId,
      createdAt: party.createdAt,
      excludePartyId: party.id,
    });
    if (!source) {
      continue;
    }
    assignments.push({ partyId: party.id, splitFromPartyId: source.id });
  }

  return assignments;
}

export function resolvePartyUnlockSourceFromCheckout(
  metadataSource: string | null | undefined,
): "stripe" | "split_prompt" {
  return metadataSource === SPLIT_PROMPT_CHECKOUT_SOURCE
    ? SPLIT_PROMPT_CHECKOUT_SOURCE
    : "stripe";
}

export function isPartyUnlockSource(
  value: string | null | undefined,
): value is "stripe" | "apple_iap" | "google_play" | "admin" | "split_prompt" {
  return (
    value === "stripe" ||
    value === "apple_iap" ||
    value === "google_play" ||
    value === "admin" ||
    value === SPLIT_PROMPT_CHECKOUT_SOURCE
  );
}

export function isStripeCollectedUnlockSource(
  value: string | null | undefined,
): boolean {
  return value === "stripe" || value === SPLIT_PROMPT_CHECKOUT_SOURCE;
}

export function splitPromptConversionRatePct(input: {
  shown: number;
  paid: number;
}): number {
  if (input.shown <= 0) {
    return 0;
  }
  return Math.round((input.paid / input.shown) * 10000) / 100;
}

export function toCappedFreeOwnedParty(party: {
  id: string;
  name: string;
  awardShowId: string;
  plan?: string | null;
  memberCount: number;
  memberCap: number | null;
  blockedJoinCount?: number;
}): CappedFreeOwnedParty | null {
  if (normalizePartyPlan(party.plan) !== "free") {
    return null;
  }
  if (party.memberCap === null || party.memberCap < 1) {
    return null;
  }
  if (!isPartyAtFreeCap(party)) {
    return null;
  }
  return {
    id: party.id,
    name: party.name,
    awardShowId: party.awardShowId,
    memberCount: party.memberCount,
    memberCap: party.memberCap,
  };
}

export function getSplitPromptCheckoutCancelPath(awardShowId: string): string {
  const trimmed = awardShowId.trim();
  return `/parties/create?showId=${encodeURIComponent(trimmed)}`;
}

export function getSplitPromptPartyCancelPath(partyId: string): string {
  return `/parties/${partyId.trim()}`;
}

export function isSafeAppRelativePath(path: string): boolean {
  const trimmed = path.trim();
  return (
    trimmed.startsWith("/") &&
    !trimmed.startsWith("//") &&
    !trimmed.includes("\\")
  );
}

export function resolvePartyPassCheckoutCancelPath(input: {
  cancelPath?: string | null;
  fromSplitPrompt: boolean;
  awardShowId: string;
  partyId: string;
}): string {
  const requested = input.cancelPath?.trim() ?? "";
  if (requested && isSafeAppRelativePath(requested)) {
    return requested;
  }
  if (input.fromSplitPrompt) {
    return getSplitPromptCheckoutCancelPath(input.awardShowId);
  }
  return getPartyPassCheckoutCancelPath(input.partyId);
}

export function classifyPartyCapDemand(input: {
  plan?: string | null;
  memberCount: number;
  freeMemberCap: number;
}): PartyCapDemand {
  const over = input.memberCount > input.freeMemberCap;
  const atOrOver = input.memberCount >= input.freeMemberCap;
  if (normalizePartyPlan(input.plan) === "party_pass") {
    return {
      freeAtCap: false,
      freeOverCap: false,
      paidOverCap: over,
    };
  }
  return {
    freeAtCap: atOrOver,
    freeOverCap: over,
    paidOverCap: false,
  };
}

export function resolveSplitCheckoutPartyId(input: {
  originalPartyId: string;
  splitPartyId?: string;
  viewingPartyId?: string;
}): string {
  return input.originalPartyId;
}

function voteMergeKey(userId: string, categoryId: string): string {
  return `${userId}:${categoryId}`;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function planPartySplitMerge(input: {
  originalParticipantUserIds: readonly string[];
  splitParticipants: readonly { id: string; userId: string }[];
  originalVoteKeys: readonly string[];
  splitVotes: readonly { id: string; userId: string; categoryId: string }[];
  originalInvitationEmails: readonly string[];
  splitInvitations: readonly { id: string; email: string }[];
  originalBlockedJoinEmails: readonly string[];
  splitBlockedJoins: readonly { id: string; email: string }[];
  originalReactionKeys?: readonly string[];
  splitReactions?: readonly { id: string; userId: string; categoryId: string }[];
}): SplitMergeMovePlan {
  const originalUsers = new Set(input.originalParticipantUserIds);
  const originalVotes = new Set(input.originalVoteKeys);
  const originalInvites = new Set(
    input.originalInvitationEmails.map(normalizeEmail),
  );
  const originalBlocked = new Set(
    input.originalBlockedJoinEmails.map(normalizeEmail),
  );
  const originalReactions = new Set(input.originalReactionKeys ?? []);

  return {
    participantIdsToMove: input.splitParticipants
      .filter((participant) => !originalUsers.has(participant.userId))
      .map((participant) => participant.id),
    voteIdsToMove: input.splitVotes
      .filter(
        (vote) => !originalVotes.has(voteMergeKey(vote.userId, vote.categoryId)),
      )
      .map((vote) => vote.id),
    invitationIdsToMove: input.splitInvitations
      .filter((invitation) => !originalInvites.has(normalizeEmail(invitation.email)))
      .map((invitation) => invitation.id),
    blockedJoinIdsToMove: input.splitBlockedJoins
      .filter((row) => !originalBlocked.has(normalizeEmail(row.email)))
      .map((row) => row.id),
    reactionIdsToMove: (input.splitReactions ?? [])
      .filter(
        (reaction) =>
          !originalReactions.has(voteMergeKey(reaction.userId, reaction.categoryId)),
      )
      .map((reaction) => reaction.id),
  };
}

function toOriginalSummary(party: OwnedPartyForSplitPrompt) {
  return {
    id: party.id,
    name: party.name,
    memberCount: party.memberCount,
    memberCap: party.memberCap ?? DEFAULT_FREE_PARTY_MEMBER_CAP,
    plan: normalizePartyPlan(party.plan),
  };
}

export function listOpenOverflowSplitParties(
  owned: readonly OwnedPartyForSplitPrompt[],
  originalId: string,
): OwnedPartyForSplitPrompt[] {
  const liveOwned = owned.filter((party) => !isMergedSplitParty(party));
  return liveOwned.filter((candidate) => {
    if (candidate.id === originalId) {
      return false;
    }
    if (candidate.splitFromPartyId === originalId) {
      return true;
    }
    const source = findSplitSourceParty(
      liveOwned.filter((party) => party.id !== candidate.id),
      {
        awardShowId: candidate.awardShowId,
        createdAt: candidate.createdAt,
        excludePartyId: candidate.id,
      },
    );
    return source?.id === originalId;
  });
}

export function findSplitPromptContext(
  owned: readonly OwnedPartyForSplitPrompt[],
  viewingPartyId: string,
): SplitPromptContext | null {
  const viewing = owned.find((party) => party.id === viewingPartyId);
  if (!viewing || isMergedSplitParty(viewing)) {
    return null;
  }

  const liveOwned = owned.filter((party) => !isMergedSplitParty(party));
  const linkedOriginal = viewing.splitFromPartyId
    ? liveOwned.find((party) => party.id === viewing.splitFromPartyId)
    : undefined;
  const detectedSource = findSplitSourceParty(
    liveOwned.filter((party) => party.id !== viewing.id),
    {
      awardShowId: viewing.awardShowId,
      createdAt: viewing.createdAt,
      excludePartyId: viewing.id,
    },
  );
  const originalFromViewing =
    linkedOriginal ??
    (detectedSource
      ? liveOwned.find((party) => party.id === detectedSource.id)
      : undefined);

  if (originalFromViewing && originalFromViewing.id !== viewing.id) {
    return {
      viewing: "split",
      checkoutPartyId: originalFromViewing.id,
      original: toOriginalSummary(originalFromViewing),
      split: {
        id: viewing.id,
        name: viewing.name,
        memberCount: viewing.memberCount,
      },
    };
  }

  const overflows = listOpenOverflowSplitParties(liveOwned, viewing.id);
  if (overflows.length === 0) {
    return null;
  }

  const split = overflows.reduce((earliest, candidate) =>
    toTime(candidate.createdAt) < toTime(earliest.createdAt)
      ? candidate
      : earliest,
  );

  return {
    viewing: "original",
    checkoutPartyId: viewing.id,
    original: toOriginalSummary(viewing),
    split: {
      id: split.id,
      name: split.name,
      memberCount: split.memberCount,
    },
  };
}
